<template>
    <div class="RiskRequirementCriterionAdvancedSettings">
        <div class="RequiredTypeItem__advancedSettingsHeader">
            <div class="RequiredTypeItem__headerRow">
                <NewButton
                    size="icon"
                    color="secondary"
                    variant="flat"
                    @click="deselectCriterion"
                >
                    <FontAwesomeIcon :icon="faAngleLeft" />
                </NewButton>
                <div class="RequiredTypeItem__titleSection">
                    <h3 class="RequiredTypeItem__advancedSettingsTitle">
                        Configure {{ currentCriterionTitle }}
                    </h3>
                    <p class="RequiredTypeItem__advancedSettingsSubtitle">
                        Set acceptance criteria and required values
                    </p>
                </div>
            </div>
        </div>
        <div class="RequiredTypeItem__advancedSettingsWarning">
            <CriteriaWarningMessage v-if="warningMessage">
                {{ warningMessage }}
            </CriteriaWarningMessage>
        </div>

        <template v-if="isCollateralCriterion">
            <CollateralCriterionSetting
                :rbac-disabled="!canManageCriteria"
                :criterion="currentCriterion"
                :template="currentTemplate"
                :show-validation-errors="showValidationErrors"
                @input="updateCriterion"
                @reference-accessed="$emit('reference-accessed', $event)"
            />
        </template>
        <CriterionSettings
            v-else
            :requirement-type-title="title"
            :criterion="currentCriterion"
            :template="currentTemplate"
            :custom-properties="customProperties"
            :country-code="countryCode"
            :show-validation-errors="showValidationErrors"
            @input="updateCriterion"
            @reference-accessed="$emit('reference-accessed', $event)"
        />
    </div>
</template>

<style lang="scss">
    @import "../variables";

    .RequiredTypeItem {
        &__advancedSettingsWarning {
            padding: $padding-y $padding-x;
        }

        &__advancedSettingsHeader {
            margin-bottom: 4px;
            padding: $padding-y $padding-x;
            border-bottom: 1px solid $border-color;
        }

        &__headerRow {
            display: flex;
            align-items: flex-start;
            gap: 12px;
        }

        &__titleSection {
            flex: 1;
        }

        &__advancedSettingsTitle {
            margin: 0 0 4px 0;
            font-size: 14px;
            font-weight: 500;
            color: var(--eidDarkBlue);
            line-height: 1.2;
        }

        &__advancedSettingsSubtitle {
            margin: 0;
            font-size: 12px;
            font-weight: 400;
            color: var(--eidSecureBlue-75);
            line-height: 1.4;
        }
    }
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { faAngleLeft } from '@fortawesome/pro-light-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { CriterionTemplate } from '@evidentid/tprm-portal-lib/models/decisioning/Criterion.model';

    import NewButton from '@/components/new-button/NewButton.vue';
    import CollateralCriterionSetting from '@/modules/decisioning-criteria/components/CollateralCriterionSetting/CollateralCriterionSetting.vue';
    import CriteriaWarningMessage from '@/modules/decisioning-criteria/components/CriteriaWarningMessage/CriteriaWarningMessage.vue';
    import CriterionSettings from '@/modules/decisioning-criteria/components/CriterionSettings/CriterionSettings.vue';
    import {
        EditableRequirementCriterion,
    } from '@/modules/decisioning-criteria/models/EditableRequirements.model';

    export default defineComponent({
        name: 'RiskRequirementCriterionAdvancedSettings',
        components: {
            CriteriaWarningMessage,
            CriterionSettings,
            CollateralCriterionSetting,
            NewButton,
            FontAwesomeIcon,
        },
        props: {
            title: {
                type: String as PropType<string>,
                default: '',
            },
            templatesByFieldName: {
                type: Object as PropType<Record<string, CriterionTemplate>>,
                default: (): Record<string, CriterionTemplate> => ({}) as Record<string, CriterionTemplate>,
            },
            customProperties: {
                type: Array as PropType<CustomProperty[]>,
                default: (): CustomProperty[] => [],
            },
            countryCode: {
                type: String as PropType<string>,
                required: false,
                default: '',
            },
            currentCriterion: {
                type: Object as PropType<EditableRequirementCriterion>,
                required: true,
            },
            criteriaList: {
                type: Array as PropType<EditableRequirementCriterion[]>,
                default: (): EditableRequirementCriterion[] => [],
            },
            warningMessage: {
                type: String as PropType<string>,
                default: '',
            },
            showValidationErrors: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            canManageCriteria: {
                type: Boolean as PropType<boolean>,
                default: true,
            },
        },
        emits: [ 'criteria-input', 'deselect-criterion', 'reference-accessed' ],
        data() {
            return {
                faAngleLeft,
            };
        },
        computed: {
            currentTemplate(): CriterionTemplate {
                return this.templatesByFieldName[this.currentCriterion.field];
            },
            currentCriterionTitle(): string {
                return this.currentTemplate?.displayMetadata?.title || '';
            },
            isCollateralCriterion(): boolean {
                return this.currentCriterion.field === 'coverage.collateral';
            },
        },
        methods: {
            deselectCriterion(): void {
                this.$emit('deselect-criterion');
            },
            updateCriterion(criterion: EditableRequirementCriterion): void {
                const updatedCriteria = this.criteriaList.map((c) =>
                    (c.field === criterion.field && c.coverageType === criterion.coverageType ? criterion : c),
                );
                this.$emit('criteria-input', updatedCriteria);
            },
        },
    });
</script>
